import { useCallback, useState } from 'react'
import { Activity, Users, Trophy, ShoppingCart } from 'lucide-react'
import AdminScreenShell, { Tegel } from '@/components/AdminScreenShell'
import { adminAPI, tournamentsAPI, kassaAPI, playersAPI } from '@/lib/api'

/* Live LAN-cijfers: wie is online, welke toernooien draaien, wat gebeurt aan de kassa. */
export default function AdminLiveScreen() {
  const [stats, setStats]   = useState(null)
  const [tours, setTours]   = useState([])
  const [players, setPlayers] = useState([])
  const [kassaTotal, setKassaTotal] = useState(null)
  const [err, setErr]       = useState(null)

  const load = useCallback(async () => {
    try {
      const [s, t, p] = await Promise.all([
        adminAPI.getEnhancedStats().catch(() => ({ data: null })),
        tournamentsAPI.getAll?.().catch(() => ({ data: { tournaments: [] } })) ??
          Promise.resolve({ data: { tournaments: [] } }),
        playersAPI.list?.().catch(() => ({ data: { players: [] } })) ??
          Promise.resolve({ data: { players: [] } }),
      ])
      setStats(s.data ?? s.data?.stats ?? null)
      setTours(t.data?.tournaments ?? [])
      setPlayers(p.data?.players ?? p.data?.users ?? [])
      // kassa-orders-count: geen endpoint (nog); tegel blijft leeg
      setKassaTotal(null)
      setErr(null)
    } catch (e) { setErr(e.message) }
  }, [])

  const online = players.filter(p => p.is_online || p.online).length
  const liveTours = tours.filter(t => t.status === 'live' || t.status === 'in_progress' || t.status === 'ongoing')

  return (
    <AdminScreenShell title="Live LAN" subtitle="Wat er nu gebeurt op deze LAN"
                      icon={Activity} accent="#B4FF39" onRefresh={load} refreshMs={8_000}>
      {err && <div className="text-signal text-sm mb-4">{err}</div>}

      <div className="grid gap-4 grid-cols-2 md:grid-cols-4 mb-6">
        <Tegel label="Online" waarde={online} sub={`${players.length} totaal`} kleur="#61E294" groot />
        <Tegel label="Toernooien" waarde={liveTours.length} sub={`${tours.length} totaal`} kleur="#E4C265" groot />
        <Tegel label="Bestellingen" waarde={kassaTotal ?? '—'} sub="vandaag" kleur="#FFB57A" groot />
        <Tegel label="Users totaal" waarde={stats?.total_users ?? players.length} sub="in de DB" kleur="#00d9ff" groot />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <section>
          <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
            <Trophy className="inline h-3 w-3 mr-1" /> Live toernooien
          </h2>
          <div className="rounded-[6px] border border-border bg-card p-4">
            {liveTours.length === 0 && <p className="text-sm text-muted-foreground">Nu geen live toernooien.</p>}
            <ul className="space-y-2">
              {liveTours.map(t => (
                <li key={t.id} className="flex items-center justify-between gap-3">
                  <span className="text-lg font-semibold truncate">{t.name}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-live">{t.status}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section>
          <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
            <Users className="inline h-3 w-3 mr-1" /> Recent gezien
          </h2>
          <div className="rounded-[6px] border border-border bg-card p-4 max-h-[52vh] overflow-y-auto">
            {players.slice(0, 20).map(p => (
              <div key={p.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-border/30 last:border-0">
                <span className="text-sm">{p.display_name || p.username}</span>
                <span className={`h-2 w-2 rounded-full ${p.is_online || p.online ? 'bg-live' : 'bg-muted-foreground'}`} />
              </div>
            ))}
          </div>
        </section>
      </div>
    </AdminScreenShell>
  )
}
